import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { masterAuth, masterDb } from '../firebase/master';
import { initClientFirebase, destroyClientFirebase } from '../firebase/clientApp';
import type { AuthState, User, Business } from '../types';

function getDeviceId(): string {
  let id = localStorage.getItem('deviceId');
  if (!id) {
    id = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem('deviceId', id);
  }
  return id;
}

function getDeviceInfo(): string {
  return navigator.userAgent.slice(0, 200);
}

// Tags a rejection with which login step it came from, so a genuine
// Firestore permission-denied (which never names the collection/document in
// its own .message, by design) still shows up as something actionable in
// the on-screen error instead of just "Authentication failed."
async function labeled<T>(p: Promise<T>, step: string): Promise<T> {
  try {
    return await p;
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code;
    const msg  = (err as { message?: string })?.message ?? String(err);
    throw new Error(`[${step}${code ? `:${code}` : ''}] ${msg}`);
  }
}

// Firestore's internal auth-token provider can lag behind onAuthStateChanged
// on the very first sign-in: the first request that actually reaches the
// server in that window goes out with request.auth == null, so ANY rule
// (even `allow read: if isOwnUser(uid)` on one's own /admins/{uid} doc)
// bounces it with permission-denied. Returning users don't see it on the
// Phase-1 reads only because persistentLocalCache serves those without a
// server round-trip; the first genuine server hit takes the bullet.
//
// So on a permission-denied we FORCE-REFRESH the ID token (which is what
// actually re-primes Firestore's credential provider) and retry, a handful
// of times over a few seconds, before giving up. Takes a factory (a
// rejected promise can't be re-awaited) and sits INSIDE `labeled` so it
// still sees the raw Firestore error's `.code`.
async function withAuthRetry<T>(fn: () => Promise<T>, tries = 6, gapMs = 400): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code !== 'permission-denied' || attempt >= tries) throw err;
      try {
        await masterAuth.currentUser?.getIdToken(true);  // force refresh
      } catch { /* offline / transient — fall through to the delay and retry */ }
      await new Promise(r => setTimeout(r, gapMs));
    }
  }
}

interface AuthContextValue extends AuthState {
  logout: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    business: null,
    isLoading: true,
    isAdmin: false,
    authError: null,
  });

  const currentUidRef      = useRef<string | null>(null);
  const sessionListenerRef = useRef<(() => void) | null>(null);
  const businessListenerRef = useRef<(() => void) | null>(null);

  function cleanupListeners() {
    sessionListenerRef.current?.();
    sessionListenerRef.current = null;
    businessListenerRef.current?.();
    businessListenerRef.current = null;
  }

  const logout = useCallback(async () => {
    const uid = currentUidRef.current;
    if (uid) {
      try {
        await updateDoc(doc(masterDb, 'sessions', uid), { isActive: false });
      } catch { /* no session doc for admin users */ }
    }
    cleanupListeners();
    // Best-effort — after the app's been open a while, the per-tenant client
    // Firestore instance can have a lot of live onSnapshot listeners (every
    // page that's ever mounted this session) still attached when this runs.
    // deleteApp() (inside destroyClientFirebase) can reject in that
    // situation, and since it wasn't wrapped, that rejection used to stop
    // execution right here — signOut(masterAuth) below never ran, so
    // clicking "Sign Out Now" silently did nothing. The client app teardown
    // is just cleanup; it must never block the actual sign-out.
    try {
      await destroyClientFirebase();
    } catch { /* stale listeners on the client app — sign out anyway */ }
    await signOut(masterAuth);
  }, []);

  const clearAuthError = useCallback(() => {
    setState(prev => ({ ...prev, authError: null }));
  }, []);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(masterAuth, async firebaseUser => {
      cleanupListeners();

      if (!firebaseUser) {
        currentUidRef.current = null;
        setState(prev => ({
          user: null,
          business: null,
          isLoading: false,
          isAdmin: false,
          authError: prev.authError,
        }));
        return;
      }

      // ── Block anonymous sign-ins ──────────────────────────────────────────
      if (firebaseUser.isAnonymous) {
        await signOut(masterAuth);
        setState({
          user: null, business: null, isLoading: false, isAdmin: false,
          authError: 'Anonymous access is not allowed.',
        });
        return;
      }

      currentUidRef.current = firebaseUser.uid;

      try {
        const uid = firebaseUser.uid;

        // Make sure a fresh ID token has actually been minted AND handed to
        // Firestore's credential provider before any read — force-refresh so
        // the SDK re-primes rather than reusing a possibly-empty credential
        // (first line of defence for the race described on `withAuthRetry`;
        // the retry loop is the fallback).
        try {
          await firebaseUser.getIdToken(true);
        } catch { /* offline — the reads below will retry / surface it */ }

        // ── Phase 1: fire all independent reads in parallel ───────────────
        // admin check + user doc + existing session — 3 reads at once instead
        // of 3 sequential round-trips.
        const [adminSnap, userSnap, sessionSnap] = await Promise.all([
          labeled(withAuthRetry(() => getDoc(doc(masterDb, 'admins',   uid))), 'admins-read'),
          labeled(withAuthRetry(() => getDoc(doc(masterDb, 'users',    uid))), 'users-read'),
          labeled(withAuthRetry(() => getDoc(doc(masterDb, 'sessions', uid))), 'sessions-read'),
        ]);

        // ── Admin check ───────────────────────────────────────────────────
        if (adminSnap.exists()) {
          setState({ user: null, business: null, isLoading: false, isAdmin: true, authError: null });
          return;
        }

        // ── User check ────────────────────────────────────────────────────
        if (!userSnap.exists()) {
          await signOut(masterAuth);
          setState({
            user: null, business: null, isLoading: false, isAdmin: false,
            authError: 'No account found. Contact admin.',
          });
          return;
        }

        const userData: User = { id: uid, ...userSnap.data() } as User;

        if (!userData.isActive) {
          await signOut(masterAuth);
          setState({
            user: null, business: null, isLoading: false, isAdmin: false,
            authError: 'Your account is disabled. Contact admin.',
          });
          return;
        }

        // ── Phase 2: session write + business fetch in parallel ───────────
        // These two operations are independent — no reason to wait for one
        // before starting the other.
        const deviceId   = getDeviceId();
        const sessionRef = doc(masterDb, 'sessions', uid);
        const businessRef= doc(masterDb, 'businesses', userData.businessId);
        const existingSess = sessionSnap.exists() ? sessionSnap.data() : null;

        const sessionWrite = labeled(
          withAuthRetry(() =>
            (existingSess?.isActive && existingSess.deviceId === deviceId)
              ? updateDoc(sessionRef, { lastActiveAt: serverTimestamp() })
              : setDoc(sessionRef, {
                  businessId:   userData.businessId,
                  deviceId,
                  deviceInfo:   getDeviceInfo(),
                  loginAt:      serverTimestamp(),
                  lastActiveAt: serverTimestamp(),
                  isActive:     true,
                })
          ),
          'session-write'
        );

        const [businessSnap] = await Promise.all([
          labeled(withAuthRetry(() => getDoc(businessRef)), 'business-read'),
          sessionWrite,
        ]);

        if (!businessSnap.exists()) throw new Error('Business record not found.');
        const businessData: Business = { id: userData.businessId, ...businessSnap.data() } as Business;

        if (!businessData.isActive) {
          await signOut(masterAuth);
          setState({
            user: null, business: null, isLoading: false, isAdmin: false,
            authError: 'Your business account is suspended. Contact admin.',
          });
          return;
        }

        // Real-time listeners (session takeover + business suspension)
        sessionListenerRef.current = onSnapshot(sessionRef, snap => {
          if (snap.exists() && !snap.data().isActive) signOut(masterAuth);
        });
        // Also keeps `business` current, so an admin's change (renewal date,
        // prices) shows in Settings › Subscription without signing in again.
        businessListenerRef.current = onSnapshot(businessRef, snap => {
          if (!snap.exists()) return;
          if (!snap.data().isActive) { signOut(masterAuth); return; }
          const fresh = { id: userData.businessId, ...snap.data() } as Business;
          setState(prev => prev.business?.id !== fresh.id
            || JSON.stringify(prev.business) === JSON.stringify(fresh) ? prev : { ...prev, business: fresh });
        });

        // ── Phase 3: init client Firebase ─────────────────────────────────
        // clientApp.ts reuses the existing connection if projectId is unchanged,
        // so this is near-instant on token refreshes / browser focus events.
        await labeled(initClientFirebase(businessData.firebaseConfig), 'client-firebase-init');

        setState({
          user: userData,
          business: businessData,
          isLoading: false,
          isAdmin: false,
          authError: null,
        });
      } catch (err) {
        console.error('Auth flow error:', err);
        // Snapshot the auth/token state at failure time — tells us whether
        // the request was genuinely unauthenticated (no currentUser / no
        // token) vs. a real rules rejection with a valid token attached.
        let authSnapshot = 'auth?';
        try {
          const cu = masterAuth.currentUser;
          if (!cu) {
            authSnapshot = 'no-currentUser';
          } else {
            const tok = await cu.getIdTokenResult().catch(() => null);
            authSnapshot = tok
              ? `uid=${cu.uid.slice(0, 6)} provider=${tok.signInProvider ?? '?'} tokenAge=${
                  Math.round((Date.now() - Date.parse(tok.issuedAtTime)) / 1000)}s`
              : `uid=${cu.uid.slice(0, 6)} token=unreadable`;
          }
        } catch { /* leave default */ }
        await signOut(masterAuth);
        const detail = err instanceof Error ? err.message : String(err);
        setState({
          user: null, business: null, isLoading: false, isAdmin: false,
          // Temporary: includes the labeled step + underlying error + the
          // auth snapshot so this is diagnosable from the screen alone (no
          // DevTools needed) while tracking down the "permission-denied"
          // report.
          authError: `Authentication failed. Please try again. (${detail} | ${authSnapshot})`,
        });
      }
    });

    return () => {
      unsubAuth();
      cleanupListeners();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, logout, clearAuthError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
